import React from "react";
import ViewDocument from "../../ui/ViewDocument";

const NepalHealth = () => {
  return (
    <div
      id="nepal"
      className="space-y-4 text-softBlack leading-loose text-base font-normal scroll-mt-24"
    >
      <p className="font-bold text-xl text-black mb-4">Nepal</p>

      <ul className="space-y-1 list-disc list-outside ml-6">
        <li>
          According to the{" "}
          <a
            href="https://cdn.who.int/media/docs/default-source/mental-health/special-initiative/nepal-simh-design-summary.pdf?sfvrsn=d8ec8ea_7"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            WHO Special Initiative for Mental Health
          </a>
          , mental illness accounts for 18 percent of all non-communicable
          diseases in Nepal. Among adult participants in Nepal’s{" "}
          <a
            href="https://nhrc.gov.np/wp-content/uploads/2022/10/National-Mental-Health-Survey-Report2020.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            National Mental Health Survey, 2020
          </a>
          , 10 percent had had a mental disorder in their lifetime. According to
          an{" "}
          <a
            href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5961374"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            independent study
          </a>{" "}
          published in 2025, Nepal has a treatment gap of 90 percent.
        </li>
        <li>
          The allocation of the national budget for mental health is very
          limited, at only 1 percent of the total health budget. It's important
          to note that the total health budget itself is only 3 percent of the
          national budget.
        </li>
        <li>
          According to the WHO, Nepal only has{" "}
          <a
            href="https://www.who.int/data/gho/data/indicators/indicator-details/GHO/psychiatrists-working-in-mental-health-sector-(per-100-000)"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            0.356 psychiatrists
          </a>{" "}
          and{" "}
          <a
            href="https://www.who.int/data/gho/data/indicators/indicator-details/GHO/psychologists-working-in-mental-health-sector-(per-100-000)"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            0.523 psychologists
          </a>{" "}
          per 100,000 population.
        </li>
        <li>
          A{" "}
          <a
            href="https://cdn.who.int/media/docs/default-source/mental-health/special-initiative/nepal-simh-design-summary.pdf?sfvrsn=d8ec8ea_7"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            2021 WHO document
          </a>{" "}
          also highlights the lack of training opportunities for mental health
          in the country, and a lack of systems for regulation and accreditation
          of service providers. The document also states that programs focusing
          on de-stigmatisation of mental health remain limited.
        </li>
        <li>
          According to{" "}
          <a
            href="https://publichealthupdate.com/nepal-health-facility-survey-2021final-report/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Nepal Health Facility Survey 2021
          </a>
          , only one in four healthcare facilities offered mental health
          services in 2021. Most of these facilities are located in urban areas,
          making access a major challenge.
        </li>
      </ul>

      <div className="flex flex-col items-center justify-center !mb-8">
        <img
          src="/assets/img/facility-survey.png"
          alt="Availability of mental health services"
          className=""
        />
        <div className="flex flex-col items-center justify-center mt-4 max-w-[546px]">
          <p className="text-xs text-softBlack font-normal italic">
            Note: PHCC: Primary Health Care Center, HP: Health Post, UHC: Urban
            Health Center, CHU: Community Health Unit
          </p>
          <p className="text-xs text-softBlack font-normal mt-2 italic">
            Source: <b>Fig. 9.4,</b> Nepal Health Facility Survey (2021),
            Ministry of Health and Population
          </p>
        </div>
      </div>

      <p className="font-bold text-black">Mental health policy and law</p>

      <div className="space-y-4">
        <p>
          According to a{" "}
          <a
            href="https://iris.who.int/bitstream/handle/10665/364901/9789290210214-eng.pdf?sequence=1"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            WHO report
          </a>
          , the Government of Nepal launched the National Mental Health Strategy
          and Action Plan in 2020. The strategy and action plan details actions
          for improving mental health in the country, in line with the
          government’s five-year development plan. The primary focus is
          integrating mental health services into primary healthcare and
          providing easy and equal access to quality mental healthcare.
        </p>
        <p>
          The five focus areas of the National Mental Health Strategy and Action
          Plan are:
        </p>

        <div className="flex flex-col items-center justify-center">
          <img
            src="/assets/img/annual-health-report.png"
            alt="Five strategies of National Mental Health Strategy and Action Plan"
            className=""
          />
          <p className="text-xs text-black mt-2 italic">
            Source: Fig 8.7,{" "}
            <a
              href="https://drive.google.com/file/d/12cvDnPC5ecuGHIXfq4y77pAtKxC6UP47/view"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              Annual Health Report 2079/80
            </a>
            , Government of Nepal
          </p>
        </div>
        <p>
          In Nepal, there is no law specifically focusing on mental health.
          However, sections 35 and 36 of the{" "}
          <a
            href="https://lpr.adb.org/sites/default/files/resource/%5Bnid%5D/nepal-the-act-relating-to-rights-of-persons-with-disabilities-2074-2017-english.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Rights of Persons with Disabilities 2074 Act (2017)
          </a>
          , mention service facilities to be provided to people with mental or
          psychosocial disabilities. Mental healthcare has also been included as
          a basic health service in the{" "}
          <a
            href="https://bwcimplementation.org/sites/default/files/resource/NP_Public-Health-Service-Act-2075_EN.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Public Health Services Act, 2075 (2016).
          </a>
        </p>
      </div>

      <p className="font-bold text-black">
        Some government initiatives (from the{" "}
        <a
          href="https://publichealthupdate.com/dohs-annual-health-report-2079-80/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue"
        >
          Annual Health Report
        </a>
        )
      </p>

      <div className="space-y-4">
        <p className="font-semibold italic">
          WHO’s Special Initiative for Mental Health
        </p>
        <p>
          Nepal is one of the nine countries in the{" "}
          <a
            href="https://www.who.int/initiatives/who-special-initiative-for-mental-health"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            WHO’s Special Initiative for Mental Health
          </a>
          . The special initiative aims to address the service and treatment gap
          in mental health and move towards universal health coverage. Through
          this initiative, countries are supported to create action plans and
          strengthen mental health services, integrating them into primary and
          secondary care. In collaboration with the WHO Nepal and various other
          stakeholders, the government developed a multi-year logical framework
          as part of the initiative. The Special Initiative also aims to provide
          targeted assistance to 14 districts through a revised district care
          model for mental health conditions.
        </p>

        <p className="font-semibold italic">
          Child and Adolescent Mental Health (CAMH) services
        </p>
        <p>
          The government of Nepal has also launched an initiative addressing
          common mental health challenges among children and adolescents, with
          the support of United Nations Children's Fund (UNICEF). Through this
          initiative, medical officers, pediatricians, and general practitioners
          across all seven provinces have been trained to identify and manage
          mental health issues in children and adolescents.
        </p>

        <p className="font-semibold italic">Khulla Mann</p>
        <p>
          <i>Khulla Mann</i> is part of the District Mental Health Care Program
          in Nepal. The program focuses on strengthening the primary healthcare
          system, integrating mental health services at district-level
          hospitals, Primary Health Care Centers (PHCCs), and health posts, and
          mental health promotion in the community. It was started in 2022 and
          replaces the Community Mental Health Care Package of 2017.
        </p>

        <ViewDocument
          description="This document has some notable private mental health initiatives globally and within the Nooraverse that directly or indirectly benefits caregivers. This is not an exhaustive list."
          documentUrl="https://sapient-dev-bucket-projects.s3.ap-south-1.amazonaws.com/noora-health/uploads/70480720563673099364.Caregiving-%26-mental-health_-Good-Practices.pdf"
          className="!my-8"
        />
      </div>
    </div>
  );
};

export default NepalHealth;
